import { useState, useEffect, useRef, useCallback } from 'react';
import { useNoteMutations } from './data/useNoteMutations';
import { useOnlineStatus } from './useOnlineStatus';
import {
  resolveNoteAutosaveStatus,
  NoteAutosaveStatus,
} from '../utils/noteAutosaveStatus';
import {
  writeNoteDraft,
  readNoteDraft,
  clearNoteDraft,
  NoteDraft,
} from '../utils/noteDraft';

const DEBOUNCE_MS = 1000;

export interface NoteSnapshot {
  note_title: string;
  note_content: string;
}

interface UseNoteAutosaveArgs {
  noteId: string;
  /** Server's current last_modified (for draft-vs-server reconcile on load). */
  serverLastModified: string;
  /** The live in-editor values. */
  current: NoteSnapshot;
  /** Whether the current values differ from what's persisted. */
  dirty: boolean;
  /** Called right after a successful save so the caller can clear its dirty flag. */
  onSaved?: () => void;
}

interface UseNoteAutosaveReturn {
  status: NoteAutosaveStatus;
  /** A recovered draft awaiting the user's Restore/Discard decision, if any. */
  pendingDraft: NoteDraft | null;
  restoreDraft: () => NoteDraft | null;
  discardDraft: () => void;
  /** Marks the current values as persisted (called by the model after a save). */
  markSaved: () => void;
}

/**
 * Autosave controller for a single open note.
 *
 * Online: debounced (1s) save via updateNote; status reflects saving/saved/error.
 * Offline: DB save disabled; edits are held in a per-note localStorage draft and
 * status shows offline/draft. On reconnect, a pending draft is flushed if it is
 * strictly newer than the server copy, then cleared.
 *
 * On mount, an existing draft is surfaced as `pendingDraft` for the caller to
 * prompt Restore/Discard rather than silently overwriting server content.
 */
export function useNoteAutosave({
  noteId,
  serverLastModified,
  current,
  dirty,
  onSaved,
}: UseNoteAutosaveArgs): UseNoteAutosaveReturn {
  const { updateNote } = useNoteMutations();
  const { isOnline } = useOnlineStatus();
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<NoteDraft | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest values, read inside the debounce callback without re-arming on every keystroke.
  const currentRef = useRef(current);
  currentRef.current = current;
  const wasOnlineRef = useRef(isOnline);

  // On mount / note switch: surface any existing draft for a restore prompt.
  useEffect(() => {
    const existing = readNoteDraft(noteId);
    setPendingDraft(existing);
    setHasDraft(!!existing);
    setError(false);
    setSaving(false);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [noteId]);

  const doSave = useCallback(async () => {
    setSaving(true);
    const snap = currentRef.current;
    const result = await updateNote(noteId, {
      note_title: snap.note_title,
      note_content: snap.note_content,
    });
    setSaving(false);
    if (result.success) {
      setError(false);
      clearNoteDraft(noteId);
      setHasDraft(false);
      onSavedRef.current?.();
    } else {
      setError(true);
    }
  }, [noteId, updateNote]);

  // React to edits: debounce a save (online) or write a draft (offline).
  useEffect(() => {
    if (!dirty) return;

    if (!isOnline) {
      // Offline: persist a local draft immediately (no debounce needed).
      const snap = currentRef.current;
      const draft: NoteDraft = {
        note_title: snap.note_title,
        note_content: snap.note_content,
        updated_at: new Date().toISOString(),
      };
      writeNoteDraft(noteId, draft);
      setHasDraft(true);
      return;
    }

    // Online: debounce a DB save. Reads latest values from currentRef at fire
    // time, so we intentionally don't depend on `current` (a new object each
    // render) — that would needlessly re-arm the timer on every keystroke.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSave();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, isOnline, noteId, doSave]);

  // On reconnect: flush a pending draft if it's newer, then clear it.
  useEffect(() => {
    const cameOnline = isOnline && !wasOnlineRef.current;
    wasOnlineRef.current = isOnline;
    if (!cameOnline) return;

    const draft = readNoteDraft(noteId);
    if (!draft) return;

    // Flush the draft's content to the DB, then clear it.
    (async () => {
      setSaving(true);
      const result = await updateNote(noteId, {
        note_title: draft.note_title,
        note_content: draft.note_content,
      });
      setSaving(false);
      if (result.success) {
        setError(false);
        clearNoteDraft(noteId);
        setHasDraft(false);
        onSavedRef.current?.();
      } else {
        setError(true);
      }
    })();
  }, [isOnline, noteId, updateNote]);

  const restoreDraft = useCallback((): NoteDraft | null => {
    const draft = pendingDraft;
    setPendingDraft(null);
    return draft;
  }, [pendingDraft]);

  const discardDraft = useCallback(() => {
    clearNoteDraft(noteId);
    setPendingDraft(null);
    setHasDraft(false);
  }, [noteId]);

  const markSaved = useCallback(() => {
    setError(false);
  }, []);

  const status = resolveNoteAutosaveStatus({
    isOnline,
    dirty,
    saving,
    error,
    hasDraft,
  });

  return { status, pendingDraft, restoreDraft, discardDraft, markSaved };
}
